import type { MutableRefObject } from 'react';
import React, { useRef, useState } from 'react';
import { useDigitalCardConfig, useDigitalCardState } from 'src/components/_common/DigitalCard/_hooks/useDigitalCard';
import { useImageLoad } from 'src/components/_common/DigitalCard/_hooks/useImageLoad';
import { calculateSpritePositions } from 'src/utils/image';
import styled from 'styled-components';

import { useAnimation } from './_hook/useAnimation';

const DEFAULT_DURATION = 6;
const DEFAULT_SCALE = 1;

export interface SpriteImageEffectProps {
  effectWidth: number;
  effectHeight: number;
  effectFrameCount: number;
  spriteImage: string;
  duration: number;
  scale?: number;
  translate?: string;
}

/**
 * [스프라이트 시트 기반의 애니메이션 컴포넌트]
 *
 * @description
 * Canvas를 사용하여 스프라이트 시트의 각 프레임을 순차적으로 렌더링하는
 * 애니메이션 효과 컴포넌트입니다. 반응형 스케일링과 최적화된 렌더링을 지원합니다.
 *
 * @param props
 * ```ts
 * {
 *   effectWidth: number,      // 각 프레임의 너비
 *   effectHeight: number,     // 각 프레임의 높이
 *   effectFrameCount: number, // 총 프레임 수
 *   spriteImage: string,         // 스프라이트 시트 이미지 경로
 *   duration: number,            // 애니메이션 재생 시간 (초 기본값: 6)
 *   scale?: number,              // 크기 배율 (기본값: 1)
 *   translate?: string           // 위치 변환 CSS 값 (기본값: 'translate(-50%, -50%)')
 * }
 * ```
 */
const SpriteImageEffect = ({
  effectWidth,
  effectHeight,
  effectFrameCount,
  spriteImage,
  duration = DEFAULT_DURATION,
  scale = DEFAULT_SCALE,
  translate,
}: SpriteImageEffectProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const spriteImageRef = useRef<HTMLImageElement | null>(null) as MutableRefObject<HTMLImageElement | null>;
  const [isSpriteLoaded, setIsSpriteLoaded] = useState(false);

  const { spriteImageLoadRetryCount, spriteImageLoadDelayTime } = useDigitalCardConfig();
  const { isAllResourcesLoaded } = useDigitalCardState();

  useImageLoad({
    imageRef: spriteImageRef,
    useImageObject: true,
    src: spriteImage,
    onLoadComplete: () => setIsSpriteLoaded(true),
    retryCount: spriteImageLoadRetryCount,
    delayTime: spriteImageLoadDelayTime,
  });

  // 스프라이트 시트의 각 프레임 좌표 계산
  const coordinates = calculateSpritePositions({
    w: effectWidth,
    h: effectHeight,
    count: effectFrameCount,
  });

  // 프레임 애니메이션 처리
  useAnimation({
    isReady: isAllResourcesLoaded && isSpriteLoaded,
    coordinates,
    frameCount: effectFrameCount,
    duration,
    width: effectWidth,
    height: effectHeight,
    canvasRef,
    spriteImageRef,
  });

  return (
    <Container $width={effectWidth} $height={effectHeight} $scale={scale} $translate={translate}>
      <canvas
        ref={canvasRef}
        width={effectWidth}
        height={effectHeight}
        style={{
          opacity: isAllResourcesLoaded && isSpriteLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease',
          width: '100%',
          aspectRatio: `${effectWidth}/${effectHeight}`,
        }}
      />
    </Container>
  );
};

const Container = styled.div<{
  $width?: number;
  $height?: number;
  $scale?: number;
  $translate?: string;
}>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: ${({ $translate, $scale }) =>
    `${$translate ? `translate(${$translate})` : 'translate(-50%, -50%)'}${$scale ? ` scale(${$scale})` : ''}`};
  width: 100%;
  aspect-ratio: ${({ $width, $height }) => `${$width}/${$height}`};
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
`;

export default SpriteImageEffect;
